/* The Beastbound Ranger's companion: what each level's training and new Experiences
   change, following the Ranger Companion sheet. */

import { COMPANION_SHEET } from './content/sheetText';
import { Catalog, featureList } from './catalog';
import { Character, CompanionTraining, LevelUp, TIER_ACHIEVEMENT_LEVELS } from './model';

/** Vicious steps: damage starts at d6 and range at Melee. */
export const COMPANION_DICE = ['d6', 'd8', 'd10', 'd12'];
export const COMPANION_RANGES = ['Melee', 'Very Close', 'Close', 'Far', 'Very Far'];

/** True when the character's subclass (or multiclass foundation) gives them a companion. */
export function hasCompanion(c: Character, catalog: Catalog) {
  const foundations = [
    catalog.cls(c.className)?.subclasses?.[c.subclass],
    c.multiclass ? catalog.cls(c.multiclass.className)?.subclasses?.[c.multiclass.subclass] : undefined,
  ];
  return foundations.some((s) => featureList(s?.foundation).some((f) => f.name === 'Companion'));
}

const trainingOf = (c: Character) => c.levelUps.flatMap((l) => (l.companion ? [l.companion] : []));

const countOf = (list: CompanionTraining[], option: string, step?: CompanionTraining['step']) =>
  list.filter((t) => t.option === option && (!step || t.step === step)).length;

/** The character sheet's Hope slots: the printed six, plus one once the companion trains Light in the Dark. */
export const hopeSlots = (c: Character, catalog: Catalog) => 6 + (hasCompanion(c, catalog) ? countOf(trainingOf(c), 'Light in the Dark') : 0);

/** The sheet's Training list with the boxes already marked; `available` while one is free. */
export function trainingOptions(c: Character) {
  const taken = trainingOf(c);
  return COMPANION_SHEET.options.map((o) => {
    const used = countOf(taken, o.name);
    return { ...o, used, available: used < o.boxes };
  });
}

/** The companion's numbers and Experiences after every level's training. */
export function companionStats(c: Character) {
  const taken = trainingOf(c);
  const names = [...c.companionExperiences, ...c.levelUps.filter((l) => l.companionExperience !== undefined).map((l) => l.companionExperience ?? '')];
  const training: Record<string, number> = {};
  taken.forEach((t) => {
    training[t.option] = (training[t.option] ?? 0) + 1;
  });
  return {
    evasion: 10 + 2 * countOf(taken, 'Aware'),
    stress: 3 + countOf(taken, 'Resilient'),
    damage: COMPANION_DICE[Math.min(countOf(taken, 'Vicious', 'damage'), COMPANION_DICE.length - 1)],
    range: COMPANION_RANGES[Math.min(countOf(taken, 'Vicious', 'range'), COMPANION_RANGES.length - 1)],
    experiences: names.map((name, i) => ({
      name: name.trim(),
      bonus: 2 + taken.filter((t) => t.option === 'Intelligent' && t.experience === i).length,
    })),
    training,
  };
}

/** What a level up still needs for the companion, in the order the step asks for it. */
export function companionProblems(l: LevelUp): string[] {
  const t = l.companion;
  return [
    TIER_ACHIEVEMENT_LEVELS.includes(l.level) && !l.companionExperience?.trim() && "Name your companion's new Experience.",
    !t && "Choose your companion's training.",
    t?.option === 'Intelligent' && t.experience === undefined && 'Pick the Companion Experience to raise.',
    t?.option === 'Vicious' && !t.step && 'Choose damage or range for Vicious.',
  ].filter((p): p is string => !!p);
}
